<script setup lang="ts">
import AuthFlow from '@/components/shared/AuthFlow.vue';
import SelectUserOverlay from '@/overlays/SelectUserOverlay.vue';
import DateTimeSelectOverlay from '@/views/booking/DateTimeSelectOverlay.vue';
import { onMounted, ref } from 'vue';
import { useBookingResourcesStore } from '@/stores/bookingResources';
import { IProfile } from '@/interfaces';

const bookingResourcesStore = useBookingResourcesStore();

const emit = defineEmits<{ close: []; success: []; error: [] }>();

interface Props {
  employeeOnly?: boolean;
  resource: any;
}

const props = withDefaults(defineProps<Props>(), {
  employeeOnly: false,
});

const showAuthFlow = ref<boolean>(false);
const showDateTimeSelectOverlay = ref<boolean>(false);
const showSelectUserOverlay = ref<boolean>(false);
const selectedProfiles = ref<IProfile[]>();
const loading = ref<boolean>(false);

const closeFlow = () => {
  showAuthFlow.value = false;
  showDateTimeSelectOverlay.value = false;
  emit('close');
};

const onAuthorized = () => {
  showAuthFlow.value = false;
  showDateTimeSelectOverlay.value = true;
};

const profilesSelected = (profiles: IProfile[]) => {
  selectedProfiles.value = profiles;
};

const onConfirmBooking = async () => {
  try {
    loading.value = true;
    bookingResourcesStore.setNewBooking({
      participants: selectedProfiles.value,
    });

    await bookingResourcesStore.createNewBooking();
    await bookingResourcesStore.getBookingResources();

    emit('success');
  } catch {
    emit('error');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (['PASSWORD', 'EMPLOYEE_ONLY'].includes(props.resource.authentication)) {
    showAuthFlow.value = true;
  } else {
    showDateTimeSelectOverlay.value = true;
  }
});
</script>

<template>
  <AuthFlow
    v-if="showAuthFlow"
    :shared-departments="resource.departments"
    :title="$t('booking.bookResource')"
    :title-bold="resource.title"
    :employee-only="props.resource.authentication === 'EMPLOYEE_ONLY'"
    @close="closeFlow"
    @authenticated="onAuthorized"
  />

  <DateTimeSelectOverlay
    v-if="showDateTimeSelectOverlay"
    :resource="props.resource"
    @close="closeFlow"
    @next="showSelectUserOverlay = true"
  />

  <SelectUserOverlay
    v-if="showSelectUserOverlay"
    :multi-select="true"
    :square-tiles="true"
    :participants="[]"
    :shared-departments="props.resource.departments"
    :title="$t('booking.selectParticipantsTitle')"
    :sub-title="$t('booking.selecttParticipantsDescription')"
    textAlignment="center"
    :confirm-button-text="$t('booking.confirmBooking')"
    :loading="loading"
    @profiles-selected="profilesSelected"
    @close="showSelectUserOverlay = false"
    @confirm="onConfirmBooking"
  />
</template>
